import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { GetLineItems } from 'smg/customer-api/lineitems';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import * as models from 'smg/customer-api/models';
import * as formatting from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

interface quoteGroup {
    latestQuoteSent: models.QuoteSent;
    quotes: models.Quote[];
}

function quoteSentMs(quoteSent: models.QuoteSent): number {
    if (quoteSent.created?.isValid) {
        return quoteSent.created.toMillis();
    }
    return 0;
}

function isImageAttachment(attachment: models.QuoteSentAttachment): boolean {
    return IsImageURL(attachment.name || attachment.key);
}

// Returns undefined for sends with neither a message nor attachments so the
// group can skip its card but still render its quotes.
function quoteSentCard(quoteSent: models.QuoteSent, subtle: boolean): DocumentFragment | undefined {
    const content = quoteSent.content?.trim() ?? '';
    const subject = quoteSent.subject?.trim() ?? '';
    const attachments = quoteSent.attachments.filter(a => Boolean(a.publicURL));
    // Subject-only sends still render a header card — the subject often
    // carries the send's intent (e.g. preliminary vs action needed).
    if (!content && !subject && !attachments.length) {
        return undefined;
    }

    const el = template.Clone('#quote-sent-template');

    if (subtle) {
        const $card = el.querySelector('.quote-sent') as HTMLElement;
        $card.classList.remove('shadow-sm');
        $card.classList.add('opacity-80');
    }

    const firstName = quoteSent.user?.firstName?.trim() ?? '';
    (el.querySelector('.quote-sent-from') as HTMLElement).textContent = firstName ?
        `A message from ${firstName}` :
        'A message from our team';

    const $date = el.querySelector('.quote-sent-date') as HTMLElement;
    if (quoteSent.created?.isValid) {
        $date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
    } else {
        $date.remove();
    }

    const $body = el.querySelector('.quote-sent-body') as HTMLElement;
    if (content) {
        (el.querySelector('.quote-sent-subject') as HTMLElement).textContent = quoteSent.subject?.trim() || 'Your Quote Is Ready';
        if (quoteSent.contentType === 'text/html') {
            $body.innerHTML = Linkify(SanitizeRichHTML(content));
        } else {
            $body.innerHTML = Linkify(html.Nl2br(html.Escape(content)));
        }
    } else {
        // Attachment-only send: the card is just its header and the files.
        $body.remove();
        el.querySelector('.quote-sent-subject')?.remove();
    }

    const $attachments = el.querySelector('.quote-sent-attachments') as HTMLElement;
    if (!attachments.length) {
        $attachments.remove();
    } else {
        const images = attachments.filter(isImageAttachment);
        const files = attachments.filter(a => !isImageAttachment(a));

        // One lightbox gallery per send instance.
        const fancyboxGroup = `quote-sent-${quoteSent.quoteSentInstanceID || quoteSent.id || ''}`;

        const $images = $attachments.querySelector('.quote-sent-attachment-images') as HTMLElement;
        if (!images.length) {
            $images.remove();
        }
        for (const attachment of images) {
            const a = template.Clone('#quote-sent-attachment-image-template').firstElementChild as HTMLAnchorElement;
            const name = attachment.name || 'Image';
            a.href = attachment.publicURL;
            a.setAttribute('data-fancybox', fancyboxGroup);
            a.setAttribute('data-caption', name);

            const img = a.querySelector('img') as HTMLImageElement;
            img.alt = name;
            img.title = name;

            $images.appendChild(a);

            // data-fancybox must already be on the anchor (done above, before
            // the append) for Picture's fancyboxThumb to wire the lightbox URL.
            void Picture(img, attachment.publicURL, {
                displayWidth: 80,
                alt: name,
                title: name,
                fancyboxThumb: true,
            });
        }

        const $files = $attachments.querySelector('.quote-sent-attachment-files') as HTMLElement;
        if (!files.length) {
            $files.remove();
        } else {
            if (images.length) {
                $files.classList.add('mt-2');
            }
            for (const attachment of files) {
                const a = template.Clone('#quote-sent-attachment-file-template').firstElementChild as HTMLAnchorElement;
                a.href = attachment.publicURL;
                (a.querySelector('.quote-sent-attachment-file-name') as HTMLElement).textContent = attachment.name || 'Attachment';

                const $size = a.querySelector('.quote-sent-attachment-file-size') as HTMLElement;
                if (attachment.size) {
                    $size.textContent = formatting.HumanFileSize(attachment.size);
                } else {
                    $size.remove();
                }

                $files.appendChild(a);
            }
        }
    }

    return el;
}

// Dated boundary between send-instance groups: every group after the newest
// gets one (even when its message card is absent) so sections can't merge.
function quoteGroupBoundary(quoteSent?: models.QuoteSent): DocumentFragment {
    const el = template.Clone('#quote-group-boundary-template');
    const $text = el.querySelector('.quote-group-boundary-text') as HTMLElement;
    if (quoteSent?.created?.isValid) {
        $text.textContent = `Sent ${quoteSent.created.toLocaleString(DateTime.DATETIME_MED)}`;
    } else {
        $text.textContent = 'Not yet emailed';
    }
    return el;
}

main();

function main() {
    const container = document.querySelector('#quotes-container') as HTMLElement;
    const spinner = document.querySelector('#quotes-spinner') as HTMLElement;
    const searchParent = document.querySelector('#search-parent') as HTMLElement;
    const searchEl = document.querySelector('#quote-search') as HTMLInputElement;

    const currQuery = QueryString().get('q');
    let q: string = currQuery || '';
    const quoteID = QueryString().get('id');
    const quoteSentInstanceID = QueryString().get('instance');

    const showSearch = () => {
        if (currQuery) {
            searchEl.value = currQuery;
        }

        searchParent.classList.remove('d-none');
        spinner.parentElement?.removeChild(spinner);
    };

    if (quoteID) {
        if (quoteID.length === 43) { // key
            q = `key:${quoteID}`;
        } else {
            q = `id:${quoteID}`;
        }
    } else if (quoteSentInstanceID) {
        if (quoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${quoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${quoteSentInstanceID}`;
        }
    } else if (!q) {
        showSearch();
        return;
    }

    OpenQuotes({
        sort: 'created asc',
        max: 100,
        data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,quoteSents',
        q,
    }).then(async data => {
        if (!data.results.length) {
            if (currQuery) {
                const emptySearchWarning = document.querySelector('#empty-search-warning') as HTMLElement;
                emptySearchWarning.classList.remove('d-none');
            }

            showSearch();
            return;
        }

        const backToSearch = document.querySelector('#back-to-search') as HTMLElement;
        if (!currQuery) {
            backToSearch.parentElement?.removeChild(backToSearch);
        }

        const f = document.createDocumentFragment();

        const appendQuoteCards = async (quotes: models.Quote[]) => {
            for (const quote of quotes) {
                const el = template.Clone('#quote-template');

                const serial = el.querySelector('.quote-serial') as HTMLElement;
                serial.textContent = String(quote.serial);
                const summary = el.querySelector('.quote-summary') as HTMLElement;
                summary.appendChild(await lineitems.Table(quote.quoteLineItems));

                const div = document.createElement('div');
                div.textContent = '*Sales tax not included in total';
                div.classList.add('w-100', 'text-end', 'fst-italic');
                summary.append(div);

                const proofsCount = quote.quoteProofs.length;

                const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
                const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
                const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
                if (quote.quoteProofs.length) {
                    for (const op of quote.quoteProofs) {
                        const p = op.proof;
                        if (!p) {
                            continue;
                        }

                        const urls = GetProofURLs(window.cloudFront, p.file || '');

                        const el = template.Clone('#quote-proof-template').firstElementChild as HTMLElement;

                        const a = el.querySelector('a') as HTMLAnchorElement;
                        a.href = urls.main;
                        a.setAttribute('data-fancybox', quote.id || '');

                        const img = a.querySelector('img') as HTMLImageElement;
                        img.src = urls.main;
                        img.alt = op.name || '';

                        const $proofName = el.querySelector('.proof-name') as HTMLElement | null;
                        if (op.name) {
                            if ($proofName) {
                                $proofName.textContent = op.name;
                                $proofName.title = op.name;
                            }
                        } else {
                            $proofName?.remove();
                        }

                        proofsParent.appendChild(el);
                    }

                    revisionLink.href = `/revisions/?id=${quoteID || quote.key}`;
                }

                const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
                addToCart.addEventListener('click', () => {
                    buttons.Start(addToCart);
                    void carts.AddCartItem({ quoteID: quote.key }, {
                        promptToCheckout: data.results.length !== 1,
                    }).then(() => {
                        addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                        addToCart.classList.add('btn-outline-success');

                        addToCart.querySelector('i')?.classList.remove('fa-shopping-cart');
                        addToCart.querySelector('i')?.classList.add('fa-check');
                    }).finally(() => {
                        buttons.Stop(addToCart);
                        if (data.results.length === 1) {
                            window.location.href = Relref('/cart');
                        }
                    });
                });

                const cardBody = el.querySelector('.card-body') as HTMLElement;

                if (proofsCount) {
                    cardBody.classList.add('row-cols-lg-2');

                    revisionLink.href = `/revisions/?id=${quoteID || quote.key}`;

                    if (proofsCount > 1) {
                        proofsParent.classList.add('row-cols-lg-2');
                    }
                } else {
                    proofsContainer.parentElement?.removeChild(proofsContainer);
                    revisionLink.parentElement?.removeChild(revisionLink);
                }

                f.appendChild(el);
            }
        };

        const groups = new Map<string, quoteGroup>();
        const unsentQuotes: models.Quote[] = [];

        for (const quote of data.results) {
            let latestQuoteSent: models.QuoteSent | undefined;
            let latestQuoteSentMs = -1;
            for (const quoteSent of quote.quoteSents) {
                const ms = quoteSentMs(quoteSent);
                if (ms > latestQuoteSentMs) {
                    latestQuoteSentMs = ms;
                    latestQuoteSent = quoteSent;
                }
            }

            if (!latestQuoteSent) {
                unsentQuotes.push(quote);
                continue;
            }

            const key = latestQuoteSent.quoteSentInstanceID || latestQuoteSent.id || '';
            const group = groups.get(key);
            if (group) {
                group.quotes.push(quote);
                if (latestQuoteSentMs > quoteSentMs(group.latestQuoteSent)) {
                    group.latestQuoteSent = latestQuoteSent;
                }
            } else {
                groups.set(key, { latestQuoteSent, quotes: [quote] });
            }
        }

        const sortedGroups = [...groups.values()].sort((a, b) => quoteSentMs(b.latestQuoteSent) - quoteSentMs(a.latestQuoteSent));

        let firstGroup = true;
        for (const group of sortedGroups) {
            if (!firstGroup) {
                f.appendChild(quoteGroupBoundary(group.latestQuoteSent));
            }

            const $sentCard = quoteSentCard(group.latestQuoteSent, !firstGroup);
            if ($sentCard) {
                f.appendChild($sentCard);
            }

            await appendQuoteCards(group.quotes);

            firstGroup = false;
        }

        if (unsentQuotes.length && !firstGroup) {
            f.appendChild(quoteGroupBoundary());
        }

        await appendQuoteCards(unsentQuotes);

        spinner.parentElement?.removeChild(spinner);
        container.appendChild(f);
        container.classList.remove('d-none');
    }).catch(err => {
        showSearch();
        throw err;
    });

    void GetLineItems();
}